<template>
  <div class="certificate">
    <div
      class="certificate__content"
      :class="[
        !haveVouchers && 'certificate__content--no-vouchers',
        (attemptInReview || attemptPassed) &&
          'certificate__content--has-upper-badge',
      ]"
    >
      <CertStatus
        v-if="attemptInReview || attemptPassed"
        class="certificate__content-status"
        :status="statusBadgeText"
        showStatus
      />
      <img
        class="certificate__content__badge"
        :src="certification.logo_image_url"
      />
      <div class="certificate__content__details">
        <div
          v-if="isLabRunning"
          class="certificate__content__details__lab-status"
        >
          LAB RUNNING
          {{ labRunningTime }}
          <span class="certificate__content__details__lab-status__ball"></span>
        </div>
        <CertificationDetails
          :difficulty="certification.difficulty"
          :timeLimit="timeCapInSecs"
          :questions="certification.amount_questions || 0"
          :objectives="labObjectives"
        />
      </div>

      <div class="certificate__content__overview">
        <div class="certificate__content__overview__pills">
          <div
            v-if="lab"
            class="certificate__content__overview__pills__haslab-pill"
            :style="`background-color: ${learningAreaColor}`"
          >
            <LabIcon
              class="certificate__content__overview__pills__haslab-pill__icon"
            />
            Hands-on Lab!
          </div>
          <CertificationsListItemVersionBadge
            v-if="!isEJPT"
            class="certificate__content__overview__pills__version-pill"
            v-bind="{
              versions: certification.versions || emptyVersions,
              latestVersion: certification.latest_version || '',
              status: '',
            }"
          />
        </div>
        <h5 class="certificate__content__overview__name">
          {{ certification.name }}
        </h5>
        <div
          class="certificate__content__overview__recomendations"
          v-html="currentVersion.recommendations_html"
        />
      </div>

      <div class="certificate__cta-wrapper">
        <div class="certificate__messages">
          <div
            v-if="canNotTakeExamForCert || !startAvailable"
            class="certificate__messages-purchase"
          >
            <div class="certificate__messages-icon">
              <AlertCircleIcon />
              <div v-if="!startAvailable">
                The exam will be available on
                <span
                  v-tooltip="{
                    content: `${availableDateTime} ${localTimeZone}`,
                    placement: 'top-center',
                  }"
                  >{{ availableOnDate }}</span
                >.
              </div>
              <div v-else>
                You have no available vouchers.
                <a
                  v-if="isBeta"
                  :href="
                    `//${certificationRedirectURL(certification.id, isBeta)}`
                  "
                  target="_blank"
                  rel="noopener nofollow"
                >
                  This is a closed beta
                </a>
              </div>
            </div>
          </div>
          <div v-if="attemptInProgress" class="certificate__messages-progress">
            <AlertCircleIcon />
            This exam is in progress. You have {{ remainingHours }} remaining.
          </div>
          <div
            v-if="canDoARetakeForCert"
            class="certificate__messages-progress"
          >
            <span class="certificate__messages-review--red"></span>
            Failed: You have
            {{ lastActiveVoucher ? lastActiveVoucher.remaining_attempts : 0 }}
            {{
              lastActiveVoucher.remaining_attempts === 1 ? 'retake' : 'retakes'
            }}
            available until
            {{ retakeExpirationShortDate }}
          </div>
          <div v-if="attemptInReview" class="certificate__messages-review">
            <span class="certificate__messages-review-status--review"></span>
            Pending Review: We're reviewing your exam. You'll get a notification
            when the results are in.
          </div>
        </div>
        <div v-if="attemptPassed" class="certificate__messages-passed">
          <router-link
            :to="{
              name: 'user-dashboard',
              params: { mode: 'certifications' },
            }"
            class="btn certificate__cta"
          >
            See your certification
          </router-link>
        </div>
        <div class="certificate__cta-wrapper__states">
          <a
            v-if="!isBeta && (!certification.user_status || canShop)"
            :href="`//${certificationRedirectURL(certification.id)}`"
            target="_blank"
            rel="noopener nofollow"
            class="btn btn--primary certificate__cta"
          >
            Shop vouchers
          </a>
          <a
            v-if="isBeta && (!certification.user_status || canShop)"
            :href="
              `${currentVersion.beta_link ||
                `//${certificationRedirectURL(certification.id, isBeta)}`}`
            "
            target="_blank"
            rel="noopener nofollow"
            class="btn btn--secondary certificate__cta"
          >
            Learn more
          </a>
          <button
            v-if="!canNotTakeExamForCert && !noCTAButton"
            class="btn btn--primary certificate__cta"
            :disabled="!startAvailable"
            @click="setCertificationAndGo()"
          >
            {{ buttonText }}
          </button>
        </div>
      </div>
    </div>
    <div v-if="haveVouchers" class="certificate__vouchers">
      <CertificateVoucher
        v-for="voucher in activeVouchersForCert"
        :key="voucher.id"
        :voucher="voucher"
        :active="true"
        class="certificate__vouchers__active"
      />
      <CertificateVoucher
        v-for="voucher in inactiveVouchersForCert"
        :key="voucher.id"
        :voucher="voucher"
        :active="false"
        class="certificate__vouchers__inactive"
      />
    </div>
  </div>
</template>
<script>
import { format } from 'date-fns-tz'
import { intervalToDuration } from 'date-fns'
import { mapState } from 'vuex'
import { AlertCircleIcon } from 'vue-feather-icons'
import CONSTANTS from '@/constants'
import {
  hasPassedAttempt,
  getLearningAreaColor,
  lastActiveVoucherFromCert,
  activeVouchers,
  inactiveVouchers,
  lastVoucher,
  attemptInProgressFromCert,
  attemptInReviewFromCert,
  attemptFailed,
  certInBeta,
  canNotTakeExam,
  noCurrentVoucher,
  canDoARetake,
  isCertAvailable,
  certAvailableDate,
  certAvailableDateTime,
  hasAnyVoucher,
  attemptRemainingHours,
} from '@/helpers'
import CertificateVoucher from '@/components/certifications/CertificateVoucher.vue'
import CertificationsListItemVersionBadge from '@/components/user-dashboard/certifications/CertificationsListItemVersionBadge.vue'
import CertificationDetails from '@/components/certifications/CertificationDetails.vue'
import CertStatus from '@/components/properties/CertStatus.vue'
import LabIcon from '@/assets/icons/course/lab/lab_icon.svg'
import { localTimezone, certificationCommons } from '@/helpers/mixins'
import { closestDate } from '@/helpers/dates'

export default {
  name: 'Certification',
  components: {
    LabIcon: { ...LabIcon, name: 'LabIcon' },
    CertificateVoucher,
    CertificationsListItemVersionBadge,
    CertificationDetails,
    CertStatus,
    AlertCircleIcon,
  },
  mixins: [localTimezone, certificationCommons],
  props: {
    learningPath: {
      type: Object,
      required: true,
    },
    certification: {
      type: Object,
      required: true,
    },
    lab: {
      type: Object,
      default: null,
    },
  },
  data: () => {
    return {
      emptyVersions: [],
    }
  },
  computed: {
    ...mapState({
      runningLabSession: state => state.labs.runningLabSession,
      isAuthenticated: state => state.auth.isAuthenticated,
    }),
    timeCapInSecs() {
      return this.$duration.hhmmss(this.certification.time_cap_in_secs)
    },
    isEJPT() {
      return this.certification.id === CONSTANTS.CERTIFICATIONS.IDS.eJPT
    },
    learningAreaColor() {
      return getLearningAreaColor(this.learningPath)
    },
    learningAreasSlugs() {
      const LAs = {
        ...CONSTANTS.LEARNING_AREAS.PRIMARY,
        ...CONSTANTS.LEARNING_AREAS.SECONDARY,
      }
      return Object.values(LAs).reduce((acc, la) => {
        acc[la.id] = la.slug
        return acc
      }, {})
    },
    canNotTakeExamForCert() {
      return canNotTakeExam(this.certification)
    },
    noCTAButton() {
      return this.attemptInReview || this.attemptPassed
    },
    canShop() {
      return noCurrentVoucher(this.certification)
    },
    attemptInReview() {
      return attemptInReviewFromCert(this.certification)
    },
    attemptPassed() {
      return hasPassedAttempt(this.lastAttempt)
    },
    attemptHasFailed() {
      return attemptFailed(this.lastAttempt)
    },
    canDoARetakeForCert() {
      return canDoARetake(this.certification)
    },
    attemptInProgress() {
      return attemptInProgressFromCert(this.certification)
    },
    lastVoucherForCert() {
      return lastVoucher(this.certification)
    },
    lastActiveVoucher() {
      return lastActiveVoucherFromCert(this.certification)
    },
    retakeExpirationShortDate() {
      return format(
        new Date(
          closestDate(
            this.lastVoucherForCert?.next_attempt_expiration_date,
            this.lastVoucherForCert?.expiration_date
          )
        ),
        'LLL d, yyyy'
      )
    },
    remainingHours() {
      return attemptRemainingHours(this.attemptInProgress)
    },
    haveVouchers() {
      return hasAnyVoucher(this.certification)
    },
    activeVouchersForCert() {
      return activeVouchers(this.certification)
    },
    inactiveVouchersForCert() {
      return inactiveVouchers(this.certification)
    },
    isBeta() {
      return certInBeta(this.certification)
    },
    labObjectives() {
      return this.lab?.lab_objectives?.length || 0
    },
    statusBadgeText() {
      if (this.attemptInReview) return 'Pending Review'
      if (this.attemptPassed) return 'awarded'
      return ''
    },
    labRunningTime() {
      const startedAt = this.lab?.session?.last_started_at

      if (startedAt) {
        return format(
          new Date(
            ...Object.values(
              intervalToDuration({
                start: new Date(startedAt),
                end: new Date(),
              })
            )
          ),
          'HH:mm:ss'
        )
      }

      return null
    },
    isLabRunning() {
      const { lab, runningLabSession } = this
      return runningLabSession && lab?.id === runningLabSession?.lab_id
    },
    startAvailable() {
      return isCertAvailable(this.certification)
    },
    availableOnDate() {
      return certAvailableDate(this.certification)
    },
    availableDateTime() {
      return certAvailableDateTime(this.certification)
    },
  },
  methods: {
    setCertificationAndGo() {
      this.$store.dispatch(
        'certifications/updateCurrentCertification',
        this.certification
      )
      this.$router.push({
        name: 'certification-exam',
        params: {
          voucherId: this.activeVouchersForCert[0].id,
          attemptId: this.attemptInProgress ? this.attemptInProgress.id : null,
        },
      })
    },
  },
}
</script>
<style lang="scss">
.certificate {
  color: var(--white);
  background: var(--glass-mid-gray);
  max-width: unset;
  display: flex;
  flex-flow: column;
  border-radius: 10px;

  &__content {
    display: flex;
    flex-flow: column;
    height: 100%;
    padding: 40px 20px;
    position: relative;

    @include mobile {
      flex-flow: row wrap;
      padding: 40px 30px;
    }

    &--no-vouchers {
      border-bottom-left-radius: 10px;
      border-bottom-right-radius: 10px;
    }
    &--has-upper-badge {
      padding-top: 40px;
    }

    &-status {
      position: absolute;
      top: 0;
      left: 0;
    }
    &__badge {
      width: 230px;
      align-self: center;
      @include desktop {
        order: 1;
        align-self: flex-start;
      }
    }
    &__details {
      margin-top: 20px;

      @include mobile {
        margin: 0 0 0 60px;
        flex: 1;
      }
      @include desktop {
        margin-left: 30px;
        width: 295px;
        order: 3;
        flex: initial;
      }

      &__lab-status {
        padding: 10px 10px 10px 15px;
        border: 1px solid var(--tundora-gray);
        box-sizing: border-box;
        border-radius: 30px;
        font-size: rem(12px);
        line-height: 14px;
        color: var(--light-blue-gray);
        display: flex;
        align-items: center;
        width: fit-content;
        margin-bottom: 20px;
        @include mobile {
          margin-left: auto;
        }
        &__ball {
          width: 13px;
          height: 13px;
          background: #70ffd4;
          border-radius: 50%;
          margin-left: 10px;
          display: block;
        }
      }
    }

    &__overview {
      margin-top: 20px;

      @include mobile {
        margin-top: 25px;
      }
      @include desktop {
        order: 2;
        flex: 1;
        margin: 0 0 0 60px;
      }

      &__pills {
        display: flex;

        @include mobile {
          margin-top: 25px;
        }

        &__haslab-pill {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 9px 14px;
          border-radius: 20px;
          color: var(--charcoal);
          margin-right: 15px;
          height: 32px;

          &__icon {
            margin-right: 10px;
            fill: var(--charcoal);
            path {
              stroke: none;
            }
          }
        }
      }
      &__name {
        margin: 15px 0 0 0;
        font-family: var(--font-bold);
        font-size: var(20px);
        line-height: var(26px);
      }
      &__recomendations {
        margin-top: 15px;

        p {
          font-family: var(--font-medium);
          font-size: var(16px);
          line-height: var(23px);
        }
        ol {
          font-family: var(--font-book);
          font-size: 14px;
          line-height: 20px;
          margin: 15px 0 !important;
          padding-inline-start: 25px;
          li {
            color: var(--light-blue-gray);
            a {
              color: var(--purple-pale);
              &:hover {
                color: var(--white);
              }
            }
            strong {
              color: var(--white);
            }
            p {
              margin: 0;
            }
          }
          ul {
            padding-left: 15px;
            li {
              list-style-type: disc;
            }
          }
        }

        ol > li::marker {
          font-weight: bold; /* Make the numbers bold */
          color: var(--white);
        }
      }
    }
  }

  &__cta-wrapper {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-content: center;
    flex: 0 1 100%;

    @include mobile {
      flex-direction: row;
      margin-top: 20px;
    }
    @include desktop {
      order: 4;
      margin-top: 40px;
    }
  }

  &__cta {
    margin-top: 20px;
    width: 100%;

    @include tablet {
      margin-top: 0px;
      width: auto;
    }
  }

  &__messages {
    display: flex;
    align-items: center;
    text-align: right;
    margin-right: auto;
    &-purchase {
      color: var(--light-blue-gray);
      display: flex;
      flex-direction: column;
      svg {
        margin-right: 15px;
        color: white;
      }
      a {
        color: var(--light-blue-gray);
        &:hover {
          color: var(--white);
        }
      }

      @include tablet {
        flex-direction: row;
      }
    }
    &-icon {
      text-align: left;
      display: flex;
      align-items: center;
    }
    &-progress {
      display: flex;
      align-items: center;
      color: var(--light-blue-gray);
      svg {
        margin-right: 15px;
      }
    }
    &-review {
      color: var(--light-blue-gray);
      display: flex;
      &-status {
        &--review {
          width: 5px;
          height: 17px;
          background: #eeeeee;
          border-radius: 10px;
          margin-right: 15px;
          border-radius: 10px;
        }
      }
      &--red {
        width: 5px;
        height: 17px;
        background: linear-gradient(180deg, #e52a2a 0%, #ff5e5e 100%);
        border-radius: 10px;
        margin-right: 15px;
        border-radius: 10px;
      }
    }
    &-passed {
      margin-bottom: 30px;
    }
    &-not-available {
      margin-right: 17px;
    }
    &__timezone-icon {
      font-size: 16px !important;
      margin-left: 5px;
      color: var(--gray);
    }
  }
}
</style>
